import type { ReactNode } from 'react';

interface TabsProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: ReactNode }>;
  ariaLabel: string;
  className?: string;
}

/** Pill-group tabs for switching panes (logs/files/…, metric windows). */
export function SegmentedTabs<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className = '',
}: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-[8px] border border-line bg-surface-2 p-1 ${className}`}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={`shrink-0 whitespace-nowrap rounded-[5px] px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60 sm:py-1 ${
            o.value === value
              ? 'bg-surface-1 text-ink shadow-sm'
              : 'text-ink-muted hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Underline tabs for page-level sections (Settings). */
export function UnderlineTabs<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className = '',
}: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      // overflow-y-hidden is load-bearing: with only overflow-x-auto, CSS computes
      // overflow-y to auto (the two axes can't be auto/visible), and the buttons'
      // -mb-px underline overlap trips a 1px phantom vertical scrollbar.
      className={`flex items-center gap-4 overflow-x-auto overflow-y-hidden border-b border-line ${className}`}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={`-mb-px border-b-2 px-1 pb-2 pt-1 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60 ${
            o.value === value
              ? 'border-signal text-ink'
              : 'border-transparent text-ink-muted hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
